import { Volume2Icon } from "@do/ui/components/animate-ui/icons/volume-2";
import { Button } from "@do/ui/components/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@do/ui/components/tooltip";
import { cn } from "@do/ui/lib/utils";
import { useTranslations } from "next-intl";
import { useCallback, useMemo } from "react";
import { useTextToSpeech } from "../../../../hooks/use-text-to-speech";
import { useChat } from "../../../../providers/chat-provider";

interface MessageActionTTSButtonProps {
  text: string;
  id: string;
}

export const MessageActionTTSButton = ({ text, id }: MessageActionTTSButtonProps) => {
  const { currentLocale, disabled } = useChat();
  const t = useTranslations("do-chat-v2");

  const { speak, stop, isSpeaking, isSupported, setMessageId, messageId } = useTextToSpeech({
    lang: currentLocale || "fr_FR",
    rate: 1,
    pitch: 1,
    volume: 0.8,
  });

  const handleTTS = useCallback(() => {
    if (!isSupported || !text) return;
    setMessageId(id);
    if (isSpeaking) {
      stop();
    } else {
      speak(text);
    }
  }, [isSupported, text, setMessageId, id, isSpeaking, stop, speak]);

  const isSpeakingCondition = useMemo(
    () => isSpeaking && messageId === id,
    [id, isSpeaking, messageId]
  );

  return (
    <Tooltip group>
      <TooltipTrigger asChild>
        <Button
          id={`message-${id}-tts-button`}
          onClick={handleTTS}
          disabled={disabled}
          size="icon"
          className={cn(
            "size-fit",
            "rounded-(--message-actions-border-radius)",
            "p-(--message-actions-padding)",
            "bg-(--message-actions-background)/(--message-actions-background-opacity)",
            "hover:bg-(--message-actions-hover-background)/(--message-actions-hover-background-opacity)",
            "text-(--message-actions-foreground)/(--message-actions-foreground-opacity)",
            "hover:text-(--message-actions-hover-foreground)/(--message-actions-hover-foreground-opacity)",
            isSpeakingCondition &&
              cn(
                "bg-(--message-actions-hover-background)/(--message-actions-hover-background-opacity)",
                "text-(--message-actions-hover-foreground)/(--message-actions-hover-foreground-opacity)"
              )
          )}
          aria-label={
            isSpeakingCondition
              ? t("chat.messages.accessibility.message-tts-stop-aria-label")
              : t("chat.messages.accessibility.message-tts-start-aria-label")
          }
          aria-pressed={isSpeakingCondition}
        >
          <Volume2Icon
            id={`message-${id}-tts-button-icon`}
            loop={isSpeakingCondition}
            animate={isSpeakingCondition}
            className="size-(--message-actions-icon-size)"
            aria-hidden="true"
          />
        </Button>
      </TooltipTrigger>

      <TooltipContent
        id={`message-${id}-tts-button-tooltip`}
        side="bottom"
        arrowClassName={cn(
          "bg-(--message-actions-tooltip-background)/(--message-actions-tooltip-background-opacity)",
          "fill-(--message-actions-tooltip-background)/(--message-actions-tooltip-background-opacity)"
        )}
        className={cn(
          "bg-(--message-actions-tooltip-background)/(--message-actions-tooltip-background-opacity)",
          "text-(--message-actions-tooltip-foreground)/(--message-actions-tooltip-foreground-opacity)"
        )}
      >
        {isSpeakingCondition
          ? t("chat.messages.actions.tts-stop")
          : t("chat.messages.actions.tts-start")}
      </TooltipContent>
    </Tooltip>
  );
};
